import React, { useState, useEffect } from 'react';
import { useAuth } from "../lib/auth.tsx";
import { supabase } from "../lib/db.ts";
import { useSettings } from "../lib/settings/settingsContext.tsx";
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  BarChart3, 
  ChevronDown,
  ChevronRight,
  Crown
} from 'lucide-react';

interface CreatorStats {
  total_earnings: number;
  total_markets: number;
  total_bets: number;
  total_volume: number;
  unique_bettors: number;
  updated_at: string;
}

interface CreatorEarning {
  id: string;
  market_id: string;
  market_title: string;
  bet_id: string;
  bettor_email: string; // Anonymized
  amount: number;
  fee_percentage: number;
  created_at: string;
}

interface CreatorMarket {
  id: string;
  title: string;
  status: string;
  is_private?: boolean;
  created_at: string;
  creator_fee_percent: number;
  total_volume?: number;
  total_bets?: number;
}

export function CreatorDashboard() {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [stats, setStats] = useState<CreatorStats | null>(null);
  const [earnings, setEarnings] = useState<CreatorEarning[]>([]);
  const [markets, setMarkets] = useState<CreatorMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [selectedMarket, setSelectedMarket] = useState<string>('all');
  const [expandedMarkets, setExpandedMarkets] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) {
      fetchCreatorData();
    }
  }, [user, timeFilter, selectedMarket]);

  const fetchCreatorData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch creator stats
      const { data: statsData, error: statsError } = await supabase.rpc('get_creator_stats', {
        creator_user_id: user.id
      });

      if (statsError) throw statsError;
      setStats(statsData);

      // Fetch creator's markets
      const { data: marketsData, error: marketsError } = await supabase
        .from('markets')
        .select(`
          id,
          title,
          status,
          is_private,
          created_at,
          creator_fee_percent
        `)
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (marketsError) throw marketsError;
      setMarkets(marketsData || []);

      // Fetch earnings with current filters
      await fetchEarnings();

    } catch (err) {
      console.error('Error fetching creator data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load creator data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    if (!user) return;

    try {
      setEarningsLoading(true);
      
      let startDate = null;
      if (timeFilter !== 'all') {
        const days = timeFilter === '7d' ? 7 : timeFilter === '30d' ? 30 : 90;
        startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
      }

      const { data: earningsData, error: earningsError } = await supabase.rpc('get_creator_earnings', {
        creator_user_id: user.id,
        p_market_id: selectedMarket === 'all' ? null : selectedMarket,
        p_start_date: startDate ? startDate.toISOString() : null,
        p_end_date: null,
        p_limit: 50,
        p_offset: 0
      });

      if (earningsError) throw earningsError;
      setEarnings(earningsData || []);

    } catch (err) {
      console.error('Error fetching earnings:', err);
      setError('Failed to load earnings data');
    } finally {
      setEarningsLoading(false);
    }
  };

  const toggleMarketExpansion = (marketId: string) => {
    setExpandedMarkets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(marketId)) {
        newSet.delete(marketId);
      } else {
        newSet.add(marketId);
      }
      return newSet;
    });
  };

  const getMarketEarnings = (marketId: string) => {
    return earnings.filter(earning => earning.market_id === marketId);
  };

  const getMarketTotalEarnings = (marketId: string) => {
    return getMarketEarnings(marketId).reduce((sum, earning) => sum + earning.amount, 0);
  };

  // Add a handler for market filter changes
  const handleMarketFilterChange = async (marketId: string) => {
    setSelectedMarket(marketId);
    // Fetch earnings will be triggered by useEffect
  };

  // Add a handler for time filter changes  
  const handleTimeFilterChange = async (newTimeFilter: string) => {
    setTimeFilter(newTimeFilter);
    // Fetch earnings will be triggered by useEffect
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8">
        <div className="text-center text-red-600">
          <p className="text-lg font-medium">Error Loading Creator Dashboard</p>
          <p className="text-sm mt-2">{error}</p>
          <button 
            onClick={fetchCreatorData}
            className="mt-4 px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-teal/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8">
        <div className="text-center text-gray-500">
          <Crown className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No Creator Data Found</p>
          <p className="text-sm mt-2">Create your first market to start earning creator commissions!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-purple-100 p-3 rounded-lg">
            <Crown className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Creator Dashboard</h1>
            <p className="text-gray-600">Track your market creation earnings and statistics</p>
          </div>
        </div>

        {/* Time Filter */}
        <div className="flex space-x-2">
          {[
            { value: 'all', label: 'All Time' },
            { value: '7d', label: 'Last 7 Days' },
            { value: '30d', label: 'Last 30 Days' },
            { value: '90d', label: 'Last 90 Days' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleTimeFilterChange(value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeFilter === value
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-700">Total Earnings</h3>
          </div>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_earnings)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-700">Markets Created</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.total_markets}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-700">Total Trades</h3>
          </div>
          <p className="text-2xl font-bold text-purple-600">{stats.total_bets}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-wayja-teal/20 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-wayja-teal" />
            </div>
            <h3 className="text-sm font-medium text-gray-700">Total Volume</h3>
          </div>
          <p className="text-2xl font-bold text-wayja-teal">{formatCurrency(stats.total_volume)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-indigo-100 p-2 rounded-lg">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-700">Unique Bettors</h3>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{stats.unique_bettors}</p>
        </div>
      </div>

      {/* Markets & Earnings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Markets */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">My Markets</h2>
            <p className="text-sm text-gray-600">Markets you've created and their performance</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {markets.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No markets created yet</p>
                <p className="text-sm">Create your first market to start earning!</p>
              </div>
            ) : (
              markets.map((market) => {
                const marketEarnings = getMarketTotalEarnings(market.id);
                const isExpanded = expandedMarkets.has(market.id);
                
                return (
                  <div key={market.id} className="border-b border-gray-100 last:border-b-0">
                    <div 
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => toggleMarketExpansion(market.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {market.title}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            {/* Privacy Badge */}
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              market.is_private
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {market.is_private ? 'Private' : 'Public'}
                            </span>
                            {/* Status Badge */}
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              market.status === 'active' 
                                ? 'bg-green-100 text-green-800'
                                : market.status === 'pending_approval'
                                ? 'bg-yellow-100 text-yellow-800'
                                : market.status === 'resolved'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {market.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {(market.creator_fee_percent * 100).toFixed(1)}% fee
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-green-600">
                            {formatCurrency(marketEarnings)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(market.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50">
                        <div className="space-y-2">
                          {getMarketEarnings(market.id).slice(0, 5).map((earning) => (
                            <div key={earning.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg">
                              <div>
                                <p className="text-xs text-gray-600">
                                  {earning.bettor_email}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(earning.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <p className="text-sm font-medium text-green-600">
                                +{formatCurrency(earning.amount)}
                              </p>
                            </div>
                          ))}
                          {getMarketEarnings(market.id).length > 5 && (
                            <p className="text-xs text-gray-500 text-center">
                              +{getMarketEarnings(market.id).length - 5} more earnings...
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Earnings */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Recent Earnings</h2>
                <p className="text-sm text-gray-600">Your latest creator commissions</p>
              </div>
              <select
                value={selectedMarket}
                onChange={(e) => handleMarketFilterChange(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1"
              >
                <option value="all">All Markets</option>
                {markets.map((market) => (
                  <option key={market.id} value={market.id}>
                    {market.title.length > 30 ? market.title.substring(0, 30) + '...' : market.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {earningsLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : earnings.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No earnings yet</p>
                <p className="text-sm">Start earning when people trade on your markets!</p>
              </div>
            ) : (
              earnings.map((earning) => (
                <div key={earning.id} className="p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {earning.market_title}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {earning.bettor_email}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {(earning.fee_percentage * 100).toFixed(1)}% commission
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-green-600">
                        +{formatCurrency(earning.amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(earning.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 